import { ChevronDown, ChevronRight, FolderIcon, FolderOpenIcon } from 'lucide-react';
import React, { useState } from 'react';
import styled from 'styled-components';
import { type FileData, FileType } from '../types';
import FileTypeIcon from './FileTypeIcon';

interface FileTreeProps {
  files: FileData[];
  onFileSelect: (file: FileData) => void;
  selectedFile: FileData | null;
  level?: number;
}

let List = styled.ul<{ level: number }>`
  margin-left: ${({ level }) => (level > 0 ? '1rem' : '0')};
`;

let ListItem = styled.li`
  margin: 0.25rem 0;
`;

let FileRow = styled.div<{ selected: boolean }>`
  display: flex;
  align-items: center;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  cursor: pointer;
  background-color: ${({ selected }) => (selected ? '#333' : 'transparent')};
  transition: background-color 0.2s ease-in-out;

  &:hover {
    background-color: ${({ selected }) => (selected ? '#333' : 'rgba(55, 55, 55, 0.5)')};
  }
`;

let IconWrapper = styled.span`
  margin-right: 0.25rem;
  width: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;

  .text-gray-400 {
    color: #999;
  }

  .text-blue-400 {
    color: #60a5fa;
  }
`;

let Name = styled.span`
  margin-left: 0.25rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
`;

let FileTree: React.FC<FileTreeProps> = ({ files, onFileSelect, selectedFile, level = 0 }) => {
  let [expandedFolders, setExpandedFolders] = useState<Record<string, boolean>>({});

  let toggleFolder = (id: string) => {
    setExpandedFolders(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  let isSelected = (file: FileData) => {
    return selectedFile?.id === file.id;
  };

  return (
    <List level={level}>
      {files
        .sort((a, b) => {
          // Sort directories first
          if (a.type === FileType.DIRECTORY && b.type !== FileType.DIRECTORY) return -1;
          if (b.type === FileType.DIRECTORY && a.type !== FileType.DIRECTORY) return 1;

          // Sort by name
          return a.name.localeCompare(b.name);
        })
        .map(file => (
          <ListItem key={file.id}>
            <FileRow
              selected={isSelected(file)}
              onClick={() => {
                if (file.type === FileType.DIRECTORY) {
                  toggleFolder(file.id);
                } else {
                  onFileSelect(file);
                }
              }}
            >
              <IconWrapper>
                {file.type === FileType.DIRECTORY ? (
                  expandedFolders[file.id] ? (
                    <ChevronDown size={16} className="text-gray-400" />
                  ) : (
                    <ChevronRight size={16} className="text-gray-400" />
                  )
                ) : (
                  <span style={{ width: 16 }} />
                )}
              </IconWrapper>

              <IconWrapper>
                {file.type === FileType.DIRECTORY ? (
                  expandedFolders[file.id] ? (
                    <FolderOpenIcon size={16} className="text-blue-400" />
                  ) : (
                    <FolderIcon size={16} className="text-blue-400" />
                  )
                ) : (
                  <FileTypeIcon fileType={file.type} />
                )}
              </IconWrapper>

              <Name>{file.name}</Name>
            </FileRow>

            {file.type === FileType.DIRECTORY && file.children && expandedFolders[file.id] && (
              <FileTree
                files={file.children}
                onFileSelect={onFileSelect}
                selectedFile={selectedFile}
                level={level + 1}
              />
            )}
          </ListItem>
        ))}
    </List>
  );
};

export default FileTree;
